import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./VisualAcuity.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const VisualAcuity = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const navigate = useNavigate();

  const [leftEye, setLeftEye] = useState("");
  const [rightEye, setRightEye] = useState("");
  const [bothEyes, setBothEyes] = useState("");

  const [message, setMessage] = useState("");
  const [glassesWorn, setGlassesWorn] = useState("");
  const [visionCorrectionType, setVisionCorrectionType] = useState("");
  const [testCondition, setTestCondition] = useState("");
  const [otherLighting, setOtherLighting] = useState("");
  const [testedAt, setTestedAt] = useState("");
  const [saving, setSaving] = useState(false);

  // ================= SERVICE =================

  const service = (localStorage.getItem("service") || "")
    .trim()
    .toLowerCase();

  const isVisionIgnite =
    service === "forge your peak - vision ignite";

  const homePath = isVisionIgnite
    ? "/measure"
    : "/ignite/dashboard";

  // ================= RETURN HOME =================

  const handleReturnHome = () => {
    navigate(homePath);
  };

  // ================= LOGIN PROTECTION =================

  useEffect(() => {
    const authed = localStorage.getItem("rg_auth") === "1";
    const email = localStorage.getItem("rg_email");

    if (!authed || !email) {
      alert("Access denied");
      navigate("/");
      return;
    }

    axios
      .get("/check-access", {
        params: {
          email,
          test: "visual_acuity",
        },
      })
      .then((res) => {
        if (!res.data.allowed) {
          alert("Access denied");
          navigate("/");
        }
      })
      .catch(() => {
        alert("Access denied");
        navigate("/");
      });
  }, [navigate]);

  // ================= SET CURRENT DATE & TIME =================

  useEffect(() => {
    const now = new Date();

    const localDateTime = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);

    setTestedAt(localDateTime);
  }, []);

  // ================= CONDITIONAL FIELD RESET =================

  useEffect(() => {
    if (glassesWorn !== "yes") {
      setVisionCorrectionType("");
    }
  }, [glassesWorn]);

  useEffect(() => {
    if (testCondition !== "Other") {
      setOtherLighting("");
    }
  }, [testCondition]);

  // ================= SAVE RESULT =================

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!leftEye || !rightEye || !bothEyes || !testedAt) {
      setMessage("Please fill in all eye results and date/time.");
      return;
    }

    if (!glassesWorn) {
      setMessage(
        "Please select whether glasses or contact lenses were worn."
      );
      return;
    }

    if (glassesWorn === "yes" && !visionCorrectionType) {
      setMessage("Please select the type of vision correction.");
      return;
    }

    if (!testCondition) {
      setMessage("Please select the test lighting condition.");
      return;
    }

    if (testCondition === "Other" && !otherLighting.trim()) {
      setMessage("Please specify the other lighting condition.");
      return;
    }

    try {
      setSaving(true);

      const email = localStorage.getItem("rg_email");

      if (!email) {
        setMessage("Session expired. Please log in again.");
        navigate("/");
        return;
      }

      const response = await axios.post("/save-visual-acuity", {
        email,
        leftEye,
        rightEye,
        bothEyes,
        glassesWorn,
        visionCorrectionType:
          glassesWorn === "yes" ? visionCorrectionType : "",
        testCondition,
        otherLighting:
          testCondition === "Other" ? otherLighting.trim() : "",
        testedAt,
      });

      if (
        response.data?.success ||
        response.data?.message ===
          "Visual acuity result saved successfully"
      ) {
        setMessage("Result saved successfully!");
      } else {
        setMessage(
          response.data?.message ||
            response.data?.error ||
            "Unable to save result."
        );
      }
    } catch (err) {
      console.error("Error saving visual acuity result:", err);

      setMessage(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving result."
      );
    } finally {
      setSaving(false);
    }
  };
  const downloadCharts = async () => {
  try {
    const zip = new JSZip();

    const basePath = "/Acuity-Charts/Acuity%20Charts/";

    const files = [
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_1.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_2.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_3.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_4.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_5.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_1.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_2.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_3.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_4.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_5.pdf",
  "readME.pdf",
];

    for (const file of files) {
      const response = await fetch(
        basePath + encodeURIComponent(file)
      );

      if (!response.ok) {
        throw new Error(`Failed to download: ${file}`);
      }

      const blob = await response.blob();

      zip.file(file, blob);
    }

    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6,
      },
    });

    saveAs(content, "Acuity-Charts.zip");
  } catch (error) {
    console.error("ZIP download error:", error);
    alert("Failed to download charts. Please try again.");
  }
};

  // ================= VISUAL ACUITY OPTIONS =================

  const acuityOptions = [
    "6/240",
    "6/120",
    "6/80",
    "6/60",
    "6/36",
    "6/24",
    "6/18",
    "6/12",
    "6/9",
    "6/6",
    "6/5",
    "6/4.5",
    "6/4",
    "6/3.5",
    "6/3",
    "6/2.5",
  ];

  // ================= UI =================

  return (
    <section className="visual-section">

      {/* ================= RETURN HOME BUTTON ================= */}

      <div className="visual-return-home-wrapper">
        <button
          type="button"
          className="visual-return-home-btn"
          onClick={handleReturnHome}
        >
          ← Return Home
        </button>
      </div>

      {/* ================= INTRO ================= */}

      <div className="visual-container">

        {isVisionIgnite ? (
          <>
            <h2 className="visual-title">
              Visual Acuity Assessment
            </h2>

            <p className="visual-subtitle">
              Visual acuity measures how clearly you can see details
              at a specified distance. This assessment helps you record
              your distance-vision results and monitor your visual
              performance.
            </p>

            <hr className="visual-line" />

            <div className="why-card">

              <h3 className="why-title">
                Before You Begin
              </h3>

              <p className="why-text">
                Follow the testing protocol provided for your program.
                Maintain the recommended distance from the visual acuity
                chart and ensure that the chart is clearly visible.
              </p>

              <ul className="why-list">
                <li>
                  Maintain the recommended testing distance.
                </li>

                <li>
                  Use consistent lighting and avoid glare.
                </li>

                <li>
                  Record whether glasses or contact lenses were used.
                </li>
              </ul>

            </div>
          </>
        ) : (
          <>
            <h2 className="visual-title">
              Visual Acuity in Sports
            </h2>

            <p className="visual-subtitle">
              Visual acuity is the ability to{" "}
              <strong>resolve fine detail</strong>. In sport it determines
              how early you pick up information and how confidently your
              brain plans movement.
            </p>

            <hr className="visual-line" />

            <div className="why-card">

              <h3 className="why-title">
                Why it matters
              </h3>

              <p className="why-text">
                Elite athletes process visual information extremely quickly.
                Higher visual acuity helps you recognize cues earlier,
                giving your brain more time to react and execute movement.
              </p>

              <div className="tag-container">
                <span>Earlier reaction</span>
                <span>Better tracking</span>
                <span>Sharper focus</span>
                <span>Faster decisions</span>
                <span>Improved timing</span>
              </div>

              <ul className="why-list">
                <li>
                  <strong>Earlier ball tracking:</strong> Detect ball
                  movement sooner.
                </li>

                <li>
                  <strong>Improved anticipation:</strong> Read opponent
                  actions faster.
                </li>

                <li>
                  <strong>More accurate timing:</strong> Strike or react
                  at the right moment.
                </li>

                <li>
                  <strong>Reduced visual fatigue:</strong> Clearer vision
                  lowers mental strain.
                </li>
              </ul>

            </div>
          </>
        )}

      </div>

      {/* ================= MEASUREMENT ================= */}

      <div className="measure-wrapper">

        <div className="measure-card">

          <h2>
            {isVisionIgnite ? "How to Measure" : "How to measure"}
          </h2>

          <ul className="measure-list">
            <li>
              Print the chart at <strong>100% size</strong>
            </li>

            <li>
              Stand at <strong>4 meters distance</strong>
            </li>

            <li>
              Cover one eye and read the smallest line
            </li>

            <li>
              Repeat for the other eye and both eyes
            </li>
          </ul>

          <div className="measure-buttons">
            <button
                type="button"
                onClick={downloadCharts}
                className="download-btn"
              >
                Download Chart
              </button>
          </div>

          <hr />

          {/* ================= RESULT FORM ================= */}

          <h3>Enter Your Result</h3>

          <form onSubmit={handleSubmit} className="result-form">

            <label>Left Eye *</label>

            <select
              value={leftEye}
              onChange={(e) => setLeftEye(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            <label>Right Eye *</label>

            <select
              value={rightEye}
              onChange={(e) => setRightEye(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            <label>Both Eyes *</label>

            <select
              value={bothEyes}
              onChange={(e) => setBothEyes(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            {/* GLASSES */}

            <label>Wearing Glasses or Contact Lenses? *</label>

            <select
              value={glassesWorn}
              onChange={(e) => setGlassesWorn(e.target.value)}
              required
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            {glassesWorn === "yes" && (
              <>
                <label>Which Vision Correction Was Used? *</label>

                <select
                  value={visionCorrectionType}
                  onChange={(e) =>
                    setVisionCorrectionType(e.target.value)
                  }
                  required
                >
                  <option value="">Select</option>
                  <option value="Glasses">Glasses</option>
                  <option value="Contact Lenses">Contact Lenses</option>
                  
                </select>
              </>
            )}

            {/* LIGHTING CONDITION */}

            <label>Test Lighting Condition *</label>

            <select
              value={testCondition}
              onChange={(e) => setTestCondition(e.target.value)}
              required
            >
              <option value="">Select Lighting Condition</option>
              <option value="Indoor Lighting">
                Indoor Lighting
              </option>
              <option value="Outdoor - Direct Sunlight">
                Outdoor - Direct Sunlight
              </option>
              <option value="Outdoor - Shade / Indirect Sunlight">
                Outdoor - Shade / Indirect Sunlight
              </option>
              <option value="Outdoor - Cloudy / Overcast">
                Outdoor - Cloudy / Overcast
              </option>
              <option value="Artificial Lighting">
                Artificial Lighting
              </option>
              <option value="Other">Other</option>
            </select>

            {testCondition === "Other" && (
              <>
                <label>Specify Other Lighting Condition *</label>

                <input
                  type="text"
                  value={otherLighting}
                  onChange={(e) => setOtherLighting(e.target.value)}
                  placeholder="Enter lighting condition"
                  required
                />
              </>
            )}

            {/* DATE & TIME */}

            <label>Date &amp; Time *</label>

            <input
              type="datetime-local"
              value={testedAt}
              onChange={(e) => setTestedAt(e.target.value)}
              className="date-input"
              required
            />

            <button
              type="submit"
              className="submit-result-btn"
              disabled={saving}
            >
              {saving ? "Saving..." : "Submit Result"}
            </button>

          </form>

          {message && (
            <div className="result-message">
              {message}
            </div>
          )}

        </div>

      </div>

    </section>
  );
};

export default VisualAcuity;